import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../config/api';
import { FaStar } from 'react-icons/fa';
import './AdminTables.css';

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    Promise.all([
      fetch(getApiUrl('/api/admin/reviews'), { headers: { Authorization: `Bearer ${token}` } }),
      fetch(getApiUrl('/api/admin/reviews/summary'), { headers: { Authorization: `Bearer ${token}` } }),
      fetch(getApiUrl('/api/admin/feedback'), { headers: { Authorization: `Bearer ${token}` } }),
    ])
      .then(([r1, r2, r3]) => Promise.all([r1.json(), r2.json(), r3.json()]))
      .then(([j1, j2, j3]) => {
        if (j1.success) setReviews(j1.reviews || []);
        if (j2.success) setSummary(j2.summary || []);
        if (j3.success) setFeedback(j3.feedback || []);
        if (!j1.success) setError(j1.message);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const updateFeedbackStatus = async (feedbackId, nextStatus) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setUpdatingId(feedbackId);
    try {
      const response = await fetch(getApiUrl(`/api/admin/feedback/${feedbackId}/status`), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: nextStatus }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to update status');
      }
      setFeedback((prev) => prev.map((item) => (item._id === feedbackId ? { ...item, status: nextStatus } : item)));
    } catch (e) {
      setError(e.message);
    } finally {
      setUpdatingId('');
    }
  };

  if (loading && reviews.length === 0 && feedback.length === 0) {
    return (
      <div className="admin-page-loading">
        <div className="admin-spinner" />
        <p>Loading reviews...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <h2 className="admin-page-title">Reviews & Feedback</h2>
      <p className="admin-page-subtitle">Guest reviews, property-wise rating summary and feedback history.</p>
      {error && <div className="admin-page-error">{error}</div>}

      <h3 className="admin-section-title">Property rating summary</h3>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Avg rating</th>
              <th>Review count</th>
            </tr>
          </thead>
          <tbody>
            {summary.map((s, i) => (
              <tr key={s.propertyId || i}>
                <td>{s.propertyName || s.propertyId || '—'}</td>
                <td><FaStar className="admin-star" /> {(s.avgRating ?? 0).toFixed(1)}</td>
                <td>{s.count ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="admin-section-title">Recent reviews</h3>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Guest</th>
              <th>Rating</th>
              <th>Comment</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {reviews.slice(0, 100).map((r) => (
              <tr key={r._id}>
                <td>{r.propertyId?.name || '—'}</td>
                <td>{r.guestName || r.userId?.email || '—'}</td>
                <td><FaStar className="admin-star" /> {r.rating}</td>
                <td className="admin-cell-truncate">{(r.comment || '').slice(0, 80)}{(r.comment || '').length > 80 ? '…' : ''}</td>
                <td>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="admin-section-title">User feedback</h3>
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Subject</th>
              <th>Message</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {feedback.map((f) => (
              <tr key={f._id}>
                <td>{f.userName || f.userId?.name || '—'}</td>
                <td>{f.userEmail || f.userId?.email || '—'}</td>
                <td>{f.subject || '—'}</td>
                <td className="admin-cell-truncate">{f.message || '—'}</td>
                <td>
                  <select
                    value={f.status || 'pending'}
                    onChange={(e) => updateFeedbackStatus(f._id, e.target.value)}
                    disabled={updatingId === f._id}
                  >
                    <option value="pending">Pending</option>
                    <option value="read">Read</option>
                    <option value="replied">Replied</option>
                  </select>
                </td>
                <td>{f.createdAt ? new Date(f.createdAt).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminReviews;
